import { accountRequest, brandingRequest } from '@frontend/account/events';
import { Account, Branding } from '@frontend/account/types';
import { attributeRequest, attributeValueRequest } from '@frontend/attribute/events';
import { Attribute, AttributeValue } from '@frontend/attribute/types';
import { basketRequest } from '@frontend/basket/events';
import { Basket } from '@frontend/basket/types';
import { budgetRequest, budgetRuleRequest } from '@frontend/budget/events';
import { Budget, BudgetRule } from '@frontend/budget/types';
import { certificateRequest, equipmentCertificateRequest, userCertificateRequest } from '@frontend/certificate/events';
import { Certificate, EquipmentCertificate, UserCertificate } from '@frontend/certificate/types';
import { colliRequest } from '@frontend/colli/events';
import { Colli } from '@frontend/colli/types';
import { EntityType, Locale, useAppSelector } from '@frontend/common';
import { constraintRequest } from '@frontend/constraint/events';
import { Constraint } from '@frontend/constraint/types';
import { costCenterRequest } from '@frontend/cost-center/events';
import { CostCenter } from '@frontend/cost-center/types';
import { deceasedRequest } from '@frontend/deceased/events';
import { IDeceased } from '@frontend/deceased/types';
import { deviceRequest } from '@frontend/device/events';
import { Device } from '@frontend/device/types';
import { documentRequest } from '@frontend/document/events';
import { Document } from '@frontend/document/types';
import { equipmentRequest } from '@frontend/equipment/events';
import { IEquipment } from '@frontend/equipment/types';
import { functionalityRequest } from '@frontend/functionality/events';
import { equipmentGroupRequest, productCategoryRequest, productGroupRequest, questionnaireRequest, userGroupRequest } from '@frontend/group/events';
import { IGroup } from '@frontend/group/types';
import { identificationRequest } from '@frontend/identification/events';
import { Identification } from '@frontend/identification/types';
import { jobRequest } from '@frontend/import/events';
import { Job } from '@frontend/import/types';
import { iotRequest } from '@frontend/iot/events';
import { IoT } from '@frontend/iot/types';
import { labelTemplateContentRequest, labelTemplateRequest } from '@frontend/label/events';
import { LabelTemplate, LabelTemplateContent } from '@frontend/label/types';
import { getCorrectTranslation } from '@frontend/lang';
import { lockerRequest } from '@frontend/locker/events';
import { Locker } from '@frontend/locker/types';
import { moduleRequest, moduleTemplateRequest } from '@frontend/module/events';
import { Module, ModuleTemplate } from '@frontend/module/types';
import { notificationContentRequest, notificationTemplateRequest } from '@frontend/notification/events';
import { NotificationContent, NotificationTemplate } from '@frontend/notification/types';
import { orderRequest } from '@frontend/order/events';
import { Order } from '@frontend/order/types';
import { packageRequest } from '@frontend/package/events';
import { Package } from '@frontend/package/types';
import { playlistItemRequest, playlistRequest } from '@frontend/playlist/events';
import { Playlist } from '@frontend/playlist/types';
import { problemRequest } from '@frontend/problem/events';
import { Problem } from '@frontend/problem/types';
import { productRequest } from '@frontend/product/events';
import { IProduct } from '@frontend/product/types';
import { Product } from '@frontend/product/utils';
import { answerRequest, questionRequest } from '@frontend/question/events';
import { Answer, Question } from '@frontend/question/types';
import { reportRequest, scheduleRequest } from '@frontend/report/events';
import { Report, Schedule } from '@frontend/report/types';
import { resolveStore } from '@frontend/repository';
import { reviewRequest } from '@frontend/review/events';
import { IReview } from '@frontend/review/types';
import { customRoleRequest } from '@frontend/role/events';
import { slotRequest, slotTemplateRequest } from '@frontend/slot/events';
import { Slot, SlotTemplate } from '@frontend/slot/types';
import { spotRequest } from '@frontend/spot/events';
import { Spot } from '@frontend/spot/types';
import { movementRequest, requirementRequest, stockRequest } from '@frontend/stock/events';
import { Movement, Requirement, Stock } from '@frontend/stock/types';
import { transactionRequest } from '@frontend/transaction/events';
import { Transaction } from '@frontend/transaction/types';
import { userInterfaceRequest } from '@frontend/user-interface/events';
import { UserInterface } from '@frontend/user-interface/types';
import { userRequest } from '@frontend/user/events';
import { IUser } from '@frontend/user/types';
import { User } from '@frontend/user/utils';
import { wizardRequest } from '@frontend/wizard/events';
import { Wizard } from '@frontend/wizard/types';
import { workflowRequest } from '@frontend/workflow/events';
import { Workflow } from '@frontend/workflow/types';
import { useSelector } from 'react-redux';

export class BadgeUtils {
    id: string;
    entityType: string;
    display?: string;
    handler?: IEntityHandler;
    constructor(id: string, entityType: string, locale?: Locale, display?: string) {
        this.id = id;
        this.entityType = entityType;
        this.display = display;
        this.handler = requestHandlers(locale)[entityType];
    }

    public resolve = (id: string): string => {
        return this.handler ? this.handler.resolve(id) : '';
    };

    public mapDisplayName = (entity: any): string | undefined => {
        if (this.display) {
            return this.display;
        } else {
            return this.handler ? this.handler.mapDisplayName(entity) : undefined;
        }
    };

    public getNavigateString = (entity: any): string => {
        return this.handler && this.handler.navigateString ? this.handler.navigateString(entity) || '' : '';
    };
}

interface IEntityHandler {
    resolve: (id: string) => string;
    mapDisplayName: (entity: any) => string | undefined;
    navigateString?: (entity: any) => string;
}

const requestHandlers = (locale?: Locale): Record<string, IEntityHandler> => {
    const resolveState = useAppSelector(useSelector, resolveStore);
    const userGroupHandler: IEntityHandler = {
        resolve: userGroupRequest,
        mapDisplayName: (entity: IGroup) => entity.name,
        navigateString: (entity: IGroup) => `/accounts/${entity.account_id}/user-groups/${entity.id}`
    };
    return {
        account: { resolve: accountRequest, mapDisplayName: (entity: Account) => entity.name, navigateString: (entity: Account) => `/accounts/${entity.id}` },
        answer: {
            resolve: answerRequest,
            mapDisplayName: (entity: Answer) => entity.answer,
            navigateString: (entity: Answer) => `/accounts/${entity.account_id}/questions/${entity.question_id}/answers/${entity.id}`
        },
        attribute: {
            resolve: attributeRequest,
            mapDisplayName: (entity: Attribute) => getCorrectTranslation(locale, entity, 'name'),
            navigateString: (entity: Attribute) => `/accounts/${entity.account_id}/attributes/${entity.id}`
        },
        attribute_value: {
            resolve: attributeValueRequest,
            mapDisplayName: (entity: AttributeValue) => entity.value
        },
        basket: {
            resolve: basketRequest,
            mapDisplayName: (entity: Basket) => entity.id
        },
        branding: {
            resolve: brandingRequest,
            mapDisplayName: (entity: Branding) => entity.domain
        },
        budget: {
            resolve: budgetRequest,
            mapDisplayName: (entity: Budget) => entity.name,
            navigateString: (entity: Budget) => `/accounts/${entity.account_id}/budgets/${entity.id}`
        },
        certificate: {
            resolve: certificateRequest,
            mapDisplayName: (entity: Certificate) => entity.code,
            navigateString: (entity: Certificate) => `/accounts/${entity.account_id}/certificates/${entity.id}`
        },
        colli: {
            resolve: colliRequest,
            mapDisplayName: (entity: Colli) => entity.label || entity.id,
            navigateString: (entity: Colli) => `/accounts/${entity.account_id}/transactions/${entity.transaction_id}/detail`
        },
        constraint: {
            resolve: constraintRequest,
            mapDisplayName: (entity: Constraint) => entity.name,
            navigateString: (entity: Constraint) => `/accounts/${entity.account_id}/constraints/${entity.id}`
        },
        cost_center: {
            resolve: costCenterRequest,
            mapDisplayName: (entity: CostCenter) => entity.name,
            navigateString: (entity: CostCenter) => `/accounts/${entity.account_id}/cost-centers/${entity.id}`
        },
        deceased: {
            resolve: deceasedRequest,
            mapDisplayName: (entity: IDeceased) => entity.name,
            navigateString: (entity: IDeceased) => `/accounts/${entity.account_id}/deceased/${entity.id}`
        },
        device: {
            resolve: deviceRequest,
            mapDisplayName: (entity: Device) => entity.name,
            navigateString: (entity: Device) => `/accounts/${entity.account_id}/iots/${entity.iot_id}/devices/${entity.id}`
        },
        document: {
            resolve: documentRequest,
            mapDisplayName: (entity: Document) => entity.name,
            navigateString: (entity: Document) => `/accounts/${entity.account_id}/documents/${entity.id}`
        },
        equipment: {
            resolve: equipmentRequest,
            mapDisplayName: (entity: IEquipment) => {
                if (!resolveState.resolved?.[EntityType.PRODUCT] || !resolveState.resolved?.[EntityType.PRODUCT][entity.product_id]) {
                    productRequest(entity.product_id);
                    return '';
                } else {
                    const productName = getCorrectTranslation(locale, resolveState.resolved[EntityType.PRODUCT][entity.product_id], 'name');
                    return `${entity.serial_number} (${productName})`;
                }
            },
            navigateString: (entity: IEquipment) => `/accounts/${entity.account_id}/equipment-products/${entity.product_id}/equipments/${entity.id}`
        },
        equipment_certificate: {
            resolve: equipmentCertificateRequest,
            mapDisplayName: (entity: EquipmentCertificate) => entity.id,
            navigateString: (entity: EquipmentCertificate) =>
                `/accounts/${entity.account_id}/equipments/${entity.equipment_id}/certificates/${entity.certificate_id}/equipment-certificates/${entity.id}`
        },
        equipment_group: {
            resolve: equipmentGroupRequest,
            mapDisplayName: (entity: IGroup) => entity.name,
            navigateString: (entity: IGroup) => `/accounts/${entity.account_id}/equipment-groups/${entity.id}`
        },
        functionality: {
            resolve: functionalityRequest,
            mapDisplayName: (entity: any) => entity.name + ' - ' + entity.application + ' - ' + entity.type,
            navigateString: (entity: any) => `/accounts/${entity.account_id}/functionalities/${entity.id}`
        },
        identification: { resolve: identificationRequest, mapDisplayName: (entity: Identification) => entity.key },
        import: {
            resolve: jobRequest,
            mapDisplayName: (entity: Job) => entity.id
        },
        iot: {
            resolve: iotRequest,
            mapDisplayName: (entity: IoT) => entity.name,
            navigateString: (entity: IoT) => `/accounts/${entity.account_id}/iots/${entity.id}`
        },
        job: {
            resolve: jobRequest,
            mapDisplayName: (entity: Job) => entity.id,
            navigateString: (entity: Job) => `/imports/${entity.id}`
        },
        locker: {
            resolve: lockerRequest,
            mapDisplayName: (entity: Locker) => entity.index.toString(),
            navigateString: (entity: Locker) => `/accounts/${entity.account_id}/iots/${entity.iot_id}/devices/${entity.device_id}/lockers/${entity.id}`
        },
        module: {
            resolve: moduleRequest,
            mapDisplayName: (entity: Module) => entity.name ?? entity.index,
            navigateString: (entity: Module) => `/accounts/${entity.account_id}/spots/${entity.spot_id}/modules/${entity.id}`
        },
        module_template: {
            resolve: moduleTemplateRequest,
            mapDisplayName: (entity: ModuleTemplate) => entity.name,
            navigateString: (entity: ModuleTemplate) => `/accounts/${entity.account_id}/module-templates/${entity.id}`
        },
        movement: {
            resolve: movementRequest,
            mapDisplayName: (entity: Movement) => entity.id,
            navigateString: (entity: Movement) =>
                `/accounts/${entity.account_id}/spots/${entity.spot_id}/modules/${entity.module_id}/slots/${entity.slot_id}/stock-updates/${entity.id}`
        },
        notification_content: {
            resolve: notificationContentRequest,
            mapDisplayName: (entity: NotificationContent) => entity.subject || entity.id,
            navigateString: (entity: NotificationContent) => `/notification-templates/${entity.template_id}/notification-contents/${entity.id}`
        },
        notification_template: {
            resolve: notificationTemplateRequest,
            mapDisplayName: (entity: NotificationTemplate) => entity.name,
            navigateString: (entity: NotificationTemplate) => `/notification-templates/${entity.id}`
        },
        order: {
            resolve: orderRequest,
            mapDisplayName: (entity: Order) => entity.number || entity.id,
            navigateString: (entity: Order) => `/accounts/${entity.account_id}/orders/${entity.id}`
        },
        label_template_content: {
            resolve: labelTemplateContentRequest,
            mapDisplayName: (entity: LabelTemplateContent) => entity.id,
            navigateString: (entity: LabelTemplateContent) => `/label-templates/${entity.template_id}/label-contents/${entity.id}`
        },
        label_template: {
            resolve: labelTemplateRequest,
            mapDisplayName: (entity: LabelTemplate) => entity.name,
            navigateString: (entity: LabelTemplate) => `/label-templates/${entity.id}`
        },
        package: {
            resolve: packageRequest,
            mapDisplayName: (entity: Package) => entity.type.replace(/_/g, ' '),
            navigateString: (entity: Package) => `/accounts/${entity.account_id}/transactions/${entity.transaction_id}/packages/${entity.id}`
        },
        playlist: {
            resolve: playlistRequest,
            mapDisplayName: (entity: Playlist) => entity.name,
            navigateString: (entity: Playlist) => `/accounts/${entity.account_id}/playlists/${entity.id}`
        },
        playlist_item: {
            resolve: playlistItemRequest,
            mapDisplayName: (entity: any) => entity.name,
            navigateString: (entity: any) => `/accounts/${entity.account_id}/playlists/${entity.playlist_id}/playlist-items/${entity.id}`
        },
        problem: {
            resolve: problemRequest,
            mapDisplayName: (entity: Problem) => entity.name,
            navigateString: (entity: Problem) => `/accounts/${entity.account_id}/problems/${entity.id}`
        },
        product: {
            resolve: productRequest,
            mapDisplayName: (entity: IProduct) => new Product(entity).getName(locale) ?? new Product(entity).getDefaultName(),
            navigateString: (entity: IProduct) => {
                return entity.type === 'equipment'
                    ? `/accounts/${entity.account_id}/equipment-products/${entity.id}`
                    : `/accounts/${entity.account_id}/products/${entity.id}`;
            }
        },
        product_category: {
            resolve: productCategoryRequest,
            mapDisplayName: (entity: IGroup) => getCorrectTranslation(locale, entity, 'name'),
            navigateString: (entity: IGroup) => `/accounts/${entity.account_id}/product-categories/${entity.id}`
        },
        product_group: {
            resolve: productGroupRequest,
            mapDisplayName: (entity: IGroup) => entity.name,
            navigateString: (entity: IGroup) => `/accounts/${entity.account_id}/product-groups/${entity.id}`
        },
        question: {
            resolve: questionRequest,
            mapDisplayName: (entity: Question) => entity.question,
            navigateString: (entity: Question) => `/accounts/${entity.account_id}/questions/${entity.id}`
        },
        questionnaire: {
            resolve: questionnaireRequest,
            mapDisplayName: (entity: IGroup) => entity.name,
            navigateString: (entity: IGroup) => `/accounts/${entity.account_id}/questionnaires/${entity.id}`
        },
        report: {
            resolve: reportRequest,
            mapDisplayName: (entity: Report) => entity.name,
            navigateString: (entity: Report) => `/accounts/${entity.account_id}/reports/${entity.id}`
        },
        requirement: {
            resolve: requirementRequest,
            mapDisplayName: (entity: Requirement) => '',
            navigateString: (entity: Requirement) => `/accounts/${entity.account_id}/spots/${entity.spot_id}/requirements/${entity.id}`
        },
        review: {
            resolve: reviewRequest,
            mapDisplayName: (entity: IReview) => entity.id,
            navigateString: (entity: IReview) => `/accounts/${entity.account_id}/reviews/${entity.id}`
        },
        role: {
            resolve: customRoleRequest,
            mapDisplayName: (entity: any) => entity.name ?? '',
            navigateString: (entity: any) => `/accounts/${entity.account_id}/roles/${entity.id}`
        },
        rule: {
            resolve: budgetRuleRequest,
            mapDisplayName: (entity: BudgetRule) => entity.name
        },
        schedule: {
            resolve: scheduleRequest,
            mapDisplayName: (entity: Schedule) => entity.name,
            navigateString: (entity: Schedule) => `/reports/${entity.report_id}/schedules/${entity.id}`
        },
        slot: {
            resolve: slotRequest,
            mapDisplayName: (entity: Slot) => entity.display || entity.index.toString(),
            navigateString: (entity: Slot) => `/accounts/${entity.account_id}/spots/${entity.spot_id}/modules/${entity.module_id}/slots/${entity.id}`
        },
        slot_template: {
            resolve: slotTemplateRequest,
            mapDisplayName: (entity: SlotTemplate) => entity.name,
            navigateString: (entity: SlotTemplate) => `/accounts/${entity.account_id}/slot-templates/${entity.id}`
        },
        spot: {
            resolve: spotRequest,
            mapDisplayName: (entity: Spot) => entity.name,
            navigateString: (entity: Spot) => `/accounts/${entity.account_id}/spots/${entity.id}`
        },
        stock: {
            resolve: stockRequest,
            mapDisplayName: (entity: Stock) => entity.id,
            navigateString: (entity: Stock) =>
                `/accounts/${entity.account_id}/spots/${entity.spot_id}/modules/${entity.module_id}/slots/${entity.slot_id}/stocks/${entity.id}`
        },
        transaction: {
            resolve: transactionRequest,
            mapDisplayName: (entity: Transaction) => entity.tracking_number || entity.id,
            navigateString: (entity: Transaction) => `/accounts/${entity.account_id}/transactions/${entity.id}`
        },
        user: {
            resolve: userRequest,
            mapDisplayName: (entity: IUser) => new User(entity).getDisplayName(),
            navigateString: (entity: IUser) => `/accounts/${entity.account_id}/users/${entity.id}`
        },
        user_certificate: {
            resolve: userCertificateRequest,
            mapDisplayName: (entity: UserCertificate) => entity.id,
            navigateString: (entity: UserCertificate) =>
                `/accounts/${entity.account_id}/users/${entity.user_id}/certificates/${entity.certificate_id}/user-certificates/${entity.id}`
        },
        group: userGroupHandler,
        user_group: userGroupHandler,
        user_interface: {
            resolve: userInterfaceRequest,
            mapDisplayName: (entity: UserInterface) => entity.name,
            navigateString: (entity: UserInterface) => `/accounts/${entity.account_id}/user-interfaces/${entity.id}`
        },
        workflow: {
            resolve: workflowRequest,
            mapDisplayName: (entity: Workflow) => entity.name,
            navigateString: (entity: Workflow) => `/accounts/${entity.account_id}/workflows/${entity.id}`
        },
        wizard: {
            resolve: wizardRequest,
            mapDisplayName: (entity: Wizard) => entity.name,
            navigateString: (entity: Wizard) => `/accounts/${entity.account_id}/wizards/${entity.id}`
        }
    };
};
